/**
 * Base fetch wrapper for all API calls.
 *
 * - Prepends `/api/` to all paths
 * - Includes credentials for session cookies
 * - Handles 401 (redirect to login), 403 (access denied), 429 (rate limit)
 * - Parses JSON responses and throws on error status codes
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public requestId?: string,
    public details?: Array<{ field: string; message: string; code: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const base = `/api/${path.replace(/^\//, '')}`;
  if (!params) return base;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `${base}?${qs}` : base;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, params, headers: customHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    'Accept': 'application/json',
    ...customHeaders as Record<string, string>,
  };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(buildUrl(path, params), {
    ...rest,
    headers,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Handle specific error statuses
  if (response.status === 401) {
    window.location.href = '/.auth/login/aad';
    throw new ApiError(401, 'UNAUTHORIZED', 'Session expired. Redirecting to login.');
  }

  if (response.status === 403) {
    throw new ApiError(403, 'FORBIDDEN', 'You do not have permission to perform this action.');
  }

  if (response.status === 429) {
    throw new ApiError(429, 'RATE_LIMITED', 'Too many requests. Please wait a moment and try again.');
  }

  // No content
  if (response.status === 204) {
    return undefined as T;
  }

  // Handle empty responses gracefully
  const text = await response.text();
  if (!text || text.trim() === '') {
    if (response.ok) {
      return (Array.isArray(undefined) ? [] : {}) as T;
    }
    throw new ApiError(response.status, 'EMPTY_RESPONSE', `Server returned an empty response (HTTP ${response.status} ${response.statusText}).`);
  }

  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    // If we got HTML back (login page redirect), the session expired
    if (text.includes('login.microsoftonline.com') || text.includes('Sign in to your account')) {
      window.location.href = '/.auth/login/aad';
      throw new ApiError(401, 'SESSION_EXPIRED', 'Session expired. Redirecting to login.');
    }
    throw new ApiError(response.status, 'INVALID_JSON', `Server returned invalid JSON: ${text.substring(0, 100)}`);
  }

  if (!response.ok) {
    const errData = data as Record<string, unknown> | null;
    const err = (errData?.error ?? {}) as Record<string, unknown>;
    throw new ApiError(
      response.status,
      (err.code as string) ?? 'UNKNOWN_ERROR',
      (err.message as string) ?? 'An unexpected error occurred.',
      err.requestId as string | undefined,
      err.details as Array<{ field: string; message: string; code: string }> | undefined,
    );
  }

  return data as T;
}

export const apiClient = {
  get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return request<T>(path, { method: 'GET', params });
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'POST', body });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PUT', body });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PATCH', body });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' });
  },
};
